import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Save, Globe, Newspaper, LayoutList, Mail, Sparkles, KeyRound } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

import { IdentityTab } from './cms-tabs/IdentityTab';
import { BlogTab } from './cms-tabs/BlogTab';
import { SectionsTab } from './cms-tabs/SectionsTab';
import { SmtpTab } from './cms-tabs/SmtpTab';
import { AiTab } from './cms-tabs/AiTab';
import { GoogleOAuthTab } from './cms-tabs/GoogleOAuthTab';

export default function CmsSettings() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/cms/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { setting_id, ...data } = settings;
      await api.put('/cms/settings', data);
      toast.success('Parametres CMS mis a jour');
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-center py-12 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="cms-settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.title')}</h1>
      </div>

      <Tabs defaultValue="identity" className="w-full">
        <TabsList className="grid w-full grid-cols-6" data-testid="cms-tabs">
          <TabsTrigger value="identity" className="text-xs sm:text-sm" data-testid="tab-identity">
            <Globe className="w-4 h-4 mr-1 hidden sm:block" /> {t('cms.identity')}
          </TabsTrigger>
          <TabsTrigger value="blog" className="text-xs sm:text-sm" data-testid="tab-blog">
            <Newspaper className="w-4 h-4 mr-1 hidden sm:block" /> Blog
          </TabsTrigger>
          <TabsTrigger value="sections" className="text-xs sm:text-sm" data-testid="tab-sections">
            <LayoutList className="w-4 h-4 mr-1 hidden sm:block" /> Sections
          </TabsTrigger>
          <TabsTrigger value="smtp" className="text-xs sm:text-sm" data-testid="tab-smtp">
            <Mail className="w-4 h-4 mr-1 hidden sm:block" /> SMTP
          </TabsTrigger>
          <TabsTrigger value="ai" className="text-xs sm:text-sm" data-testid="tab-ai">
            <Sparkles className="w-4 h-4 mr-1 hidden sm:block" /> IA
          </TabsTrigger>
          <TabsTrigger value="google" className="text-xs sm:text-sm" data-testid="tab-google">
            <KeyRound className="w-4 h-4 mr-1 hidden sm:block" /> Google
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-6">
          <IdentityTab settings={settings} update={update} onSave={handleSave} loading={saving} />
          <div className="mt-4">
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving} data-testid="cms-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {t('cms.save')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="blog" className="mt-6">
          <BlogTab />
        </TabsContent>

        <TabsContent value="sections" className="mt-6">
          <SectionsTab />
        </TabsContent>

        <TabsContent value="smtp" className="mt-6">
          <SmtpTab />
        </TabsContent>

        <TabsContent value="ai" className="mt-6">
          <AiTab />
        </TabsContent>

        <TabsContent value="google" className="mt-6">
          <GoogleOAuthTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
